import type { ApproveDecision } from '../approval-request';
import type { ProjectIconName } from '../project-icon';

/**
 * T-37: the Inbox, what's waiting on me across every project (docs/inbox-design.md). Items are worked
 * out from current state (waits, question cards, runs, PR reviews, suggestions), never stored as events,
 * so each one goes away when the work moves on. Only a person's read and Done marks are stored.
 */

/**
 * Every kind, in the order the filter and the delivery settings list them. `needs_approval` is a run's
 * coding agent waiting on a permission approval in T3 Code (SF-T-177; a flow's gates, roadmap item 4, can
 * share it later). `spec_review` (a spec to approve, roadmap item 9) isn't worked out yet: it has its
 * place in the filter, the settings and the API already.
 */
export const INBOX_KINDS = ['question', 'dispute', 'waiting', 'pr_needs_you', 'pr_review', 'suggestions', 'subtasks', 'failed_run', 'patch_request', 'needs_approval', 'spec_review'] as const;
export type InboxKind = (typeof INBOX_KINDS)[number];

/** The kinds the Inbox works out today. */
export const INBOX_LIVE_KINDS: readonly InboxKind[] = ['question', 'dispute', 'waiting', 'pr_needs_you', 'pr_review', 'suggestions', 'subtasks', 'failed_run', 'patch_request', 'needs_approval'];

export const INBOX_KIND_LABELS: Record<InboxKind, string> = {
  question: 'Question',
  /** SF-T-74: a run's disputed review findings, to settle (leave them, or send them back to fix). */
  dispute: 'Review to settle',
  waiting: 'Waiting on you',
  pr_needs_you: 'PR needs you',
  pr_review: 'PR to review',
  suggestions: 'Suggestions to decide',
  /** SF-T-178: sub-tasks the AI builder proposed, to add or reject. */
  subtasks: 'Sub-tasks to decide',
  failed_run: 'Failed run',
  /** T-57: a coding agent's patch request, for factory admins (only they decide them). */
  patch_request: 'Patch to decide',
  /** SF-T-177: a run's coding agent waits on a permission approval in T3 Code, for the run's starter. */
  needs_approval: 'Needs approval',
  spec_review: 'Spec to approve',
};

/**
 * Kinds that can be marked Done by hand, a personal mark: nothing else moves them on. The others go away
 * only when the work does (an answer, the wait removed, the suggestions decided). Suggestions have a
 * Done too (`dismissible`), but theirs decides them: No thanks on each still open (SF-T-62). So do
 * proposed sub-tasks (SF-T-178): Reject leaves them all out.
 */
export const INBOX_DISMISSIBLE_KINDS: readonly InboxKind[] = ['pr_review', 'pr_needs_you', 'failed_run'];

export const isInboxKind = (value: unknown): value is InboxKind => typeof value === 'string' && (INBOX_KINDS as readonly string[]).includes(value);

/**
 * Where an item can also be sent, besides the app (always). Slack comes later (decision 24): its column
 * shows in the settings as coming, and `slack` is stored, so it slots in.
 */
export const INBOX_CHANNELS = ['push', 'slack'] as const;
export type InboxChannel = (typeof INBOX_CHANNELS)[number];

/** One kind's delivery: the app always, plus these. Everything is off until the person switches it on. */
export interface InboxKindDelivery {
  kind: InboxKind;
  push: boolean;
  slack: boolean;
}

export interface InboxProject {
  id: string;
  slug: string;
  name: string;
  color: string;
  icon: ProjectIconName | null;
  task_prefix: string;
}

export interface InboxItem {
  /**
   * Stable while the item is the same thing waiting (`question:<wait id>`, `failed_run:<run id>`,
   * `pr_review:<task id>:<PR url>:<review time>`): read and Done marks hang on it, so a new review or
   * a new wait is a new item.
   */
  key: string;
  kind: InboxKind;
  project: InboxProject;
  task: { id: string; number: number; ref: string; title: string };
  /** The run it's about (a failed run, the run that asked, the run whose PR it is). */
  run: { id: string | null; number: number } | null;
  /** Why it's here, one line: "R-81 asked you 8 questions", "PR #53 reviewed: 3 fixed". */
  reason: string;
  /** The pull request, for the PR kinds. */
  pr_url: string | null;
  /** SF-T-266: about a live run's turn report: opening it goes to the agent's chat (?agent=chat). */
  agent_chat?: boolean;
  /** SF-T-177, needs_approval: the first line of the command waiting on approval, shown in the row after the reason. */
  command?: string;
  /** SF-T-177, needs_approval and T3 questions: the thread in T3 Code, where it can be answered. */
  t3_url?: string;
  /**
   * SF-T-225, needs_approval: the approval request and the approvals the factory can send for it (Approve,
   * Approve for session), so the row can approve it without opening the task. Absent when it can't (an older
   * agent's request): approve it on the task or in T3 Code.
   */
  approval?: { id: string; decisions: ApproveDecision[] };
  /** When it started waiting on you. */
  since: string;
  read: boolean;
  dismissible: boolean;
  /**
   * Suggestions only: the factory message whose suggestions these are (one item per message, SF-T-62).
   * Done records No thanks on what's still open in it (POST /api/tasks/:taskId/suggestions/decline).
   */
  suggestion_event_id?: string;
  /**
   * SF-T-178, sub-tasks only: the factory message whose proposed sub-tasks these are (one item per
   * message). Reject leaves them all out (POST /api/tasks/:taskId/subtask-proposals with no picks).
   */
  subtasks_event_id?: string;
  /** SF-T-74, disputes only: the factory message with the disputed findings, settled from the Inbox's Settle. */
  dispute_event_id?: string;
}

/**
 * SF-T-156: how many tasks have something unread, the sidebar's count. The Inbox shows one card per
 * task, so two unread items on one task count once.
 */
export const countUnreadInboxTasks = (items: readonly Pick<InboxItem, 'read' | 'task'>[]): number =>
  new Set(items.filter((i) => !i.read).map((i) => i.task.id)).size;

/** GET /api/inbox (and /api/v1/me/inbox): newest first. */
export interface InboxResponse {
  items: InboxItem[];
  /** Tasks with an item not yet read (countUnreadInboxTasks): the sidebar's count. */
  unread: number;
  /** Projects that couldn't be read (a database error): the rest still show. */
  failed_projects: string[];
  /**
   * SF-T-98: things across projects with no task, shown above the items (only GET /api/inbox has them). Today one
   * kind: an admin's guard catches to review (/guard-catches), gone once every one is decided (SF-T-286).
   */
  notices?: InboxNotice[];
}

export interface InboxNotice {
  key: string;
  kind: 'guard_catches';
  /** One line: "3 kinds of guard catch need a look, in 2 projects". */
  text: string;
  /** The app path it opens. */
  url: string;
  level: 'bad' | 'warn';
}

/** POST /api/inbox/marks: mark items read or unread, Done or back. */
export interface InboxMarksInput {
  /** Each item's project and key. */
  items: Array<{ project_id: string; key: string }>;
  read?: boolean;
  done?: boolean;
}

/** GET /api/inbox/settings */
export interface InboxSettingsResponse {
  kinds: InboxKindDelivery[];
  /** The server's public VAPID key for Web Push; null when push isn't set up on this server. */
  push_public_key: string | null;
  /** How many browsers this person has allowed push in. */
  push_subscriptions: number;
}

/** The push message's payload, read by the service worker (frontend/public/sw.js). */
export interface InboxPushPayload {
  title: string;
  body: string;
  /** The app path to open on a click. */
  url: string;
  /** The item's key: a second push for the same item replaces the first. */
  tag: string;
}
